import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { CtaBlock } from "../../components/CtaBlock";
import { RelatedArticles } from "../../components/RelatedArticles";

const ARTICLE = {
	title: "商品画像の背景を白にしたいのに灰色になる — Photoshop以外の解決策",
	description:
		"背景除去後の画像が灰色になる原因（透明PNG）と、Photoshop不要でブラウザだけで白背景に変換する方法を解説。",
	publishDate: "2026-03-16",
	slug: "product-image-background-gray-solution",
};

const ogImageUrl = `https://og.fucina.casa/api/og?title=${encodeURIComponent(ARTICLE.title)}&description=${encodeURIComponent(ARTICLE.description)}&locale=ja`;

export default function ProductImageBackgroundGraySolution() {
	return (
		<>
			<Helmet>
				<title>{ARTICLE.title} | しろぬきや</title>
				<meta name="description" content={ARTICLE.description} />
				<meta property="og:title" content={ARTICLE.title} />
				<meta property="og:description" content={ARTICLE.description} />
				<meta
					property="og:url"
					content={`https://shironukiya.fucina.casa/blog/${ARTICLE.slug}`}
				/>
				<meta property="og:type" content="article" />
				<meta property="og:image" content={ogImageUrl} />
				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:title" content={ARTICLE.title} />
				<meta name="twitter:description" content={ARTICLE.description} />
				<meta name="twitter:image" content={ogImageUrl} />
				<link
					rel="canonical"
					href={`https://shironukiya.fucina.casa/blog/${ARTICLE.slug}`}
				/>
				<script type="application/ld+json">
					{JSON.stringify({
						"@context": "https://schema.org",
						"@type": "Article",
						headline: ARTICLE.title,
						description: ARTICLE.description,
						datePublished: ARTICLE.publishDate,
						publisher: {
							"@type": "Organization",
							name: "しろぬきや",
							url: "https://shironukiya.fucina.casa",
						},
					})}
				</script>
			</Helmet>

			<div className="max-w-3xl mx-auto px-4 py-10">
				<nav className="text-sm text-gray-500 mb-6">
					<Link to="/" className="hover:underline">
						ホーム
					</Link>
					{" > "}
					<Link to="/blog" className="hover:underline">
						ブログ
					</Link>
					{" > "}
					<span>{ARTICLE.title}</span>
				</nav>

				<article>
					<h1 className="text-2xl font-bold mb-4">{ARTICLE.title}</h1>
					<p className="text-gray-500 text-sm mb-8">
						更新日: {ARTICLE.publishDate}
					</p>

					<section>
						<p className="text-gray-700 mb-6">
							背景除去ツールを使って商品画像を「白背景」にしようとしたのに、保存した画像が灰色に見える……この経験、ありますよね？
							実はこれ、ツールの問題ではなく「透明」と「白」の違いが原因です。
						</p>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							なぜ灰色になるのか？ 透明PNGの仕組み
						</h2>
						<p className="text-gray-700 mb-4">
							多くの背景除去ツールは、背景を「白」にするのではなく「透明」にしたPNGを出力します。透明部分は各アプリが自由に表示色を決めるため、次のような現象が起きます。
						</p>
						<ul className="list-disc list-inside space-y-2 text-gray-700 mb-4">
							<li>
								Windowsのフォトビューアー・Macプレビュー：
								透明部分をグレーや白のチェック柄で表示
							</li>
							<li>
								AmazonセラーセントラルにPNGのままアップロード：
								灰色背景として判定されエラーになる
							</li>
							<li>
								メルカリ・楽天のアップロードフォーム：
								ビューアーによっては灰色に見える
							</li>
						</ul>
						<p className="text-gray-700">
							つまり「透明PNG」は白背景ではありません。
							透明部分を「白で塗りつぶした状態」にしてからJPEGまたはPNGで保存する必要があります。
						</p>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							Photoshopなしで白背景に変換する手順
						</h2>

						<h3 className="text-lg font-medium mt-6 mb-3">
							方法1: ブラウザだけで完結（一番簡単）
						</h3>
						<ol className="list-decimal list-inside space-y-2 text-gray-700 mb-4">
							<li>しろぬきやを開く（インストール不要）</li>
							<li>商品画像をドラッグ＆ドロップ</li>
							<li>AIが自動で背景除去し、白背景で出力</li>
							<li>そのままダウンロード</li>
						</ol>
						<p className="text-gray-700 mb-4">
							しろぬきやは背景を「透明」にするのではなく、最初から「白で塗りつぶした状態」で出力します。
							各モールの規格サイズ（Amazon
							1000×1000px等）への自動リサイズも同時に行えます。
						</p>

						<h3 className="text-lg font-medium mt-6 mb-3">
							方法2: GIMP（無料・インストール型）
						</h3>
						<ol className="list-decimal list-inside space-y-2 text-gray-700 mb-4">
							<li>透明PNGをGIMPで開く</li>
							<li>メニュー「画像」→「統合画像」をクリック</li>
							<li>背景色（白）でフラット化される</li>
							<li>「ファイル」→「エクスポート」でJPEG保存</li>
						</ol>

						<h3 className="text-lg font-medium mt-6 mb-3">
							方法3: Canvaで白背景を追加
						</h3>
						<ol className="list-decimal list-inside space-y-2 text-gray-700 mb-4">
							<li>Canvaで新規デザインを作成</li>
							<li>背景を白に設定</li>
							<li>透明PNGをアップロードして配置</li>
							<li>JPEGまたはPNGでダウンロード</li>
						</ol>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							しろぬきやで解決する方法
						</h2>
						<p className="text-gray-700 mb-4">
							毎回GIMPやCanvaで手順を踏むのは手間がかかります。しろぬきやなら、背景除去から白背景への変換、ECモール規格サイズへのリサイズまでをワンクリックで完了できます。
						</p>

						<CtaBlock />
					</section>

					<RelatedArticles current={ARTICLE.slug} />
				</article>
			</div>
		</>
	);
}
